import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Package, ShoppingBag, Search, Check, X, ExternalLink, Upload, Image as ImageIcon, LayoutGrid, Plus, Trash2, RotateCcw, ArrowUpDown, Sparkles, UploadCloud, MessageCircle, Sliders, Home, ZoomIn, Ticket } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { usePrices, resolveImage } from "@/context/PricesContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Logo } from "@/components/Logo";
import { NewProductModal } from "@/components/admin/NewProductModal";
import { BulkPhotoUploader } from "@/components/admin/BulkPhotoUploader";
import { AdminChats } from "@/components/admin/AdminChats";
import { VariationSchemaEditor } from "@/components/admin/VariationSchemaEditor";
import { HomepageEditor } from "@/components/admin/HomepageEditor";
import { CouponsPanel } from "@/components/admin/CouponsPanel";
import { ImageZoomLightbox } from "@/components/admin/ImageZoomLightbox";
import { OrderDetailDrawer } from "@/components/admin/OrderDetailDrawer";

const STATUS_OPTIONS = ["initiated", "completed", "cancelled"];
const FULFILL_OPTIONS = ["pending", "processing", "shipped", "out_for_delivery", "delivered", "cancelled"];
const COURIERS = ["", "Delhivery", "DTDC", "Blue Dart", "India Post", "FedEx", "DHL", "Aramex", "UPS", "Other"];

/**
 * Safely turn any axios/FastAPI error payload into a string that `toast.error`
 * can render. FastAPI returns Pydantic 422 errors as an array of
 * `{type, loc, msg, input}` — rendering that array through `toast.error()`
 * throws React error #31 ("Objects are not valid as a React child").
 */
const formatApiError = (e, fallback = "Something went wrong") => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic v2 error list — build a friendly, one-line-per-field summary.
    return detail
      .map((row) => {
        const field = Array.isArray(row?.loc) ? row.loc.filter((s) => s !== "body").join(".") : "";
        const msg = row?.msg || "invalid value";
        return field ? `${field}: ${msg}` : msg;
      })
      .join(" · ")
      .slice(0, 300);
  }
  if (detail && typeof detail === "object") return detail.message || JSON.stringify(detail).slice(0, 300);
  return e?.message || fallback;
};

export const AdminDashboardPage = () => {
  const { me, logout, authAxios, token } = useAdminAuth();
  const { format, formatMoney } = useCurrency();
  const { refresh: refreshPublicPrices } = usePrices();
  const nav = useNavigate();

  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [orderFilter, setOrderFilter] = useState({ payment: "", fulfillment: "", from: "", to: "" });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [genRun, setGenRun] = useState(null); // { total, done, failed, current, cancel }
  const [zoomState, setZoomState] = useState(null); // { images: string[], index: number }

  const openZoom = (product) => {
    // Try to use full images array; fall back to hero.
    const src = product?.custom?.images || product?.override?.images || (product?.hero_image ? [product.hero_image] : []);
    const imgs = (src || []).filter(Boolean).map(resolveImage);
    if (!imgs.length) return;
    setZoomState({ images: imgs, index: 0 });
  };

  const load = async () => {
    setLoading(true);
    try {
      const ax = authAxios();
      const [o, p, c] = await Promise.all([
        ax.get("/admin/orders"),
        ax.get("/admin/products"),
        ax.get("/admin/categories"),
      ]);
      setOrders(o.data || []);
      setProducts(p.data || []);
      setCategories(c.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        nav("/admin/login");
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filteredOrders = useMemo(() => {
    const t = q.trim().toLowerCase();
    const { payment, fulfillment, from, to } = orderFilter;
    const fromT = from ? new Date(from + "T00:00:00Z").getTime() : null;
    const toT = to ? new Date(to + "T23:59:59Z").getTime() : null;
    return orders.filter((o) => {
      if (t) {
        const hay = [
          o.order_id, o.gateway, o.gateway_order_id, o.gateway_payment_id,
          o.buyer?.email, o.buyer?.name, o.buyer?.phone, o.buyer?.country,
          o.paypal_payer_email, o.tracking_number,
          ...(o.items || []).flatMap((it) => [it.title, it.slug, it.size, it.colour]),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(t)) return false;
      }
      if (payment && (o.payment_status || "pending") !== payment) return false;
      if (fulfillment && (o.fulfillment_status || "pending") !== fulfillment) return false;
      if (fromT || toT) {
        const at = o.created_at ? new Date(o.created_at).getTime() : 0;
        if (fromT && at < fromT) return false;
        if (toT && at > toT) return false;
      }
      return true;
    });
  }, [orders, q, orderFilter]);

  const filteredProducts = useMemo(() => {
    if (!q.trim()) return products;
    const t = q.toLowerCase();
    return products.filter((p) => p.title.toLowerCase().includes(t) || p.slug.toLowerCase().includes(t));
  }, [products, q]);

  const filteredCategories = useMemo(() => {
    if (!q.trim()) return categories;
    const t = q.toLowerCase();
    return categories.filter((c) => c.label.toLowerCase().includes(t) || c.slug.toLowerCase().includes(t));
  }, [categories, q]);

  const updateOrder = async (order_id, patch) => {
    try {
      const { data } = await authAxios().patch(`/admin/orders/${order_id}`, patch);
      setOrders((list) => list.map((o) => (o.order_id === order_id ? data : o)));
      toast.success("Order updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const bulkUpload = async (csvText) => {
    try {
      const { data } = await authAxios().post(`/admin/orders/bulk-update`, { csv: csvText });
      // Reflect updated rows in the current orders list
      const updatedMap = Object.fromEntries((data.updated || []).map((u) => [u.order_id, u]));
      setOrders((list) =>
        list.map((o) => (updatedMap[o.order_id] ? { ...o, ...updatedMap[o.order_id] } : o))
      );
      const nOK = (data.updated || []).length;
      const nErr = (data.errors || []).length;
      if (nErr) toast.warning(`${nOK} updated · ${nErr} skipped`);
      else toast.success(`${nOK} orders updated · status emails sent`);
      return data;
    } catch (e) {
      toast.error(formatApiError(e, "Bulk update failed"));
      throw e;
    }
  };

  const updatePrice = async (slug, price) => {
    try {
      const { data } = await authAxios().patch(`/admin/products/${slug}`, { price });
      setProducts((list) => list.map((p) => (p.slug === slug ? { ...p, price: data.price, override: Math.abs(data.price - p.default_price) > 0.01 } : p)));
      refreshPublicPrices();
      toast.success("Price updated");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const uploadProductImage = async (slug, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large — max 8 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await authAxios().post(`/admin/products/${slug}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProducts((list) => list.map((p) => (p.slug === slug ? { ...p, hero_image: data.url } : p)));
      refreshPublicPrices();
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed"));
    }
  };

  const resetProductImage = async (slug) => {
    try {
      await authAxios().delete(`/admin/products/${slug}/image`);
      setProducts((list) => list.map((p) => (p.slug === slug ? { ...p, hero_image: null } : p)));
      refreshPublicPrices();
      toast.success("Reverted to stock");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const generateProductImage = async (slug) => {
    try {
      const { data } = await authAxios().post(`/admin/products/${slug}/generate-image`, null, { timeout: 60000 });
      setProducts((list) => list.map((p) => (p.slug === slug ? { ...p, hero_image: data.url } : p)));
      refreshPublicPrices();
      toast.success("Studio photo generated");
    } catch (e) {
      toast.error(formatApiError(e, "Generation failed"));
    }
  };

  const generateCategoryImage = async (slug) => {
    try {
      const { data } = await authAxios().post(`/admin/categories/${slug}/generate-image`, null, { timeout: 60000 });
      setCategories((list) => list.map((c) => (c.slug === slug ? { ...c, image: data.image, override: true } : c)));
      refreshPublicPrices();
      toast.success("Tile generated");
    } catch (e) {
      toast.error(formatApiError(e, "Generation failed"));
    }
  };

  // Batch: walk items without a custom hero and AI-generate one at a time.
  // Sequential (server + LLM safety) with cancel support and per-item state updates.
  const generateAllMissing = async (kind /* "product" | "category" */) => {
    const list = kind === "product"
      ? products.filter((p) => !p.hero_image && !p.is_deleted).map((p) => p.slug)
      : categories.filter((c) => !c.image && !c.is_deleted).map((c) => c.slug);
    if (!list.length) {
      toast.info(kind === "product" ? "Every product already has a photo." : "Every category already has a tile.");
      return;
    }
    const ctrl = { cancelled: false };
    setGenRun({ total: list.length, done: 0, failed: 0, current: list[0], kind, cancel: () => (ctrl.cancelled = true) });
    let done = 0, failed = 0;
    for (const slug of list) {
      if (ctrl.cancelled) break;
      setGenRun((g) => g && { ...g, current: slug });
      try {
        if (kind === "product") await generateProductImage(slug);
        else await generateCategoryImage(slug);
        done += 1;
      } catch (e) {
        failed += 1;
      }
      setGenRun((g) => g && { ...g, done, failed });
    }
    setGenRun(null);
    const stopped = ctrl.cancelled ? " (stopped)" : "";
    toast.success(`${done} generated · ${failed} failed${stopped}`);
  };

  // Used by the bulk uploader — same as uploadProductImage but returns instead of toasting.
  const bulkUploadOne = async (slug, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await authAxios().post(`/admin/products/${slug}/image`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setProducts((list) => list.map((p) => (p.slug === slug ? { ...p, hero_image: data.url } : p)));
    refreshPublicPrices();
  };

  const uploadCategoryImage = async (slug, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large — max 8 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await authAxios().post(`/admin/categories/${slug}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCategories((list) => list.map((c) => (c.slug === slug ? { ...c, image: data.image, override: true } : c)));
      refreshPublicPrices();
      toast.success("Tile updated");
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed"));
    }
  };

  const resetCategoryImage = async (slug) => {
    try {
      await authAxios().delete(`/admin/categories/${slug}/image`);
      setCategories((list) => list.map((c) => (c.slug === slug ? { ...c, image: null, override: false } : c)));
      refreshPublicPrices();
      toast.success("Reverted to stock");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const createProduct = async (payload) => {
    try {
      await authAxios().post("/admin/products", payload);
      toast.success("Product created — you can add photos below");
      // Keep the modal open so the inline photo gallery unlocks.
      await load();
      refreshPublicPrices();
    } catch (e) {
      toast.error(formatApiError(e, "Create failed"));
      throw e;
    }
  };

  const updateProduct = async (slug, payload) => {
    try {
      await authAxios().patch(`/admin/products/${slug}`, payload);
      toast.success("Saved");
      setEditingProduct(null);
      await load();
      refreshPublicPrices();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
      throw e;
    }
  };

  const deleteProduct = async (slug) => {
    if (!window.confirm("Hide this product from the storefront? Existing orders are unaffected.")) return;
    try {
      await authAxios().delete(`/admin/products/${slug}`);
      setProducts((list) => list.map((p) => (p.slug === slug ? { ...p, is_deleted: true } : p)));
      refreshPublicPrices();
      toast.success("Hidden from storefront");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const restoreProduct = async (slug) => {
    try {
      await authAxios().post(`/admin/products/${slug}/restore`);
      setProducts((list) => list.map((p) => (p.slug === slug ? { ...p, is_deleted: false } : p)));
      refreshPublicPrices();
      toast.success("Restored");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const patchProductField = async (slug, patch) => {
    try {
      await authAxios().patch(`/admin/products/${slug}`, patch);
      setProducts((list) => list.map((p) => (p.slug === slug ? { ...p, ...patch } : p)));
      refreshPublicPrices();
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const createCategory = async ({ slug, label, sort_order }) => {
    try {
      await authAxios().post("/admin/categories", { slug, label, sort_order });
      toast.success("Category added");
      await load();
      refreshPublicPrices();
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
      throw e;
    }
  };

  const renameCategory = async (slug, label) => {
    try {
      await authAxios().patch(`/admin/categories/${slug}`, { label });
      setCategories((list) => list.map((c) => (c.slug === slug ? { ...c, label } : c)));
      refreshPublicPrices();
      toast.success("Renamed");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const updateCategorySort = async (slug, sort_order) => {
    try {
      await authAxios().patch(`/admin/categories/${slug}`, { sort_order });
      setCategories((list) => list.map((c) => (c.slug === slug ? { ...c, sort_order } : c)));
      refreshPublicPrices();
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const deleteCategory = async (slug) => {
    if (!window.confirm("Hide this category from the storefront? Products stay in the catalogue.")) return;
    try {
      await authAxios().delete(`/admin/categories/${slug}`);
      setCategories((list) => list.map((c) => (c.slug === slug ? { ...c, is_deleted: true } : c)));
      refreshPublicPrices();
      toast.success("Hidden");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  const restoreCategory = async (slug) => {
    try {
      await authAxios().post(`/admin/categories/${slug}/restore`);
      setCategories((list) => list.map((c) => (c.slug === slug ? { ...c, is_deleted: false } : c)));
      refreshPublicPrices();
      toast.success("Restored");
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  // Revenue by currency — orders may be captured in USD, INR, GBP, etc.
  // Mixing them under a single "total" would be nonsense, so we group.
  const revenueByCurrency = useMemo(() => {
    const acc = {};
    for (const o of orders) {
      if (o.payment_status !== "paid") continue;
      // Prefer the actual amount PayPal captured over the calculated subtotal.
      const amount = Number(o.paid_amount ?? o.total ?? o.subtotal ?? 0);
      const cur = o.paid_currency || o.currency || "USD";
      acc[cur] = (acc[cur] || 0) + amount;
    }
    return acc;
  }, [orders]);

  const primaryRevenueLine = useMemo(() => {
    const entries = Object.entries(revenueByCurrency).filter(([, v]) => v > 0);
    if (!entries.length) return formatMoney(0, "USD");
    // Sort by native amount desc, format each in its own currency.
    entries.sort((a, b) => b[1] - a[1]);
    return entries.map(([cur, val]) => formatMoney(val, cur)).join(" · ");
  }, [revenueByCurrency, formatMoney]);

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-white">
      {/* header */}
      <header className="border-b border-hairline sticky top-0 z-30 bg-white/90 backdrop-blur-xl">
        <div className="px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <Link to="/" className="flex items-center gap-2">
              <Logo variant="wordmark" />
            </Link>
            <span className="overline text-forest/60 border-l border-hairline pl-3">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline overline text-forest/60">{me?.email}</span>
            <Link to="/" target="_blank" className="edit-link overline text-forest/70 hidden md:inline">
              View site <ExternalLink size={12} strokeWidth={1.6} className="inline ml-1" />
            </Link>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); nav("/admin/login"); }}
              className="inline-flex items-center gap-2 overline text-forest border border-hairline rounded-full px-3 py-1.5 hover:border-forest transition-colors duration-500"
            >
              <LogOut size={12} strokeWidth={1.6} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="px-6 md:px-12 py-10 md:py-14 border-b border-hairline bg-bone">
        <div className="overline text-gold mb-3">§ Dashboard</div>
        <h1 className="font-display text-forest text-4xl md:text-6xl leading-[0.95] tracking-tight">
          {(() => {
            const h = new Date().getHours();
            const salut = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
            return (
              <>
                {salut}, <span className="font-editorial italic text-champagne">Anabiya.</span>
              </>
            );
          })()}
        </h1>
        <div className="mt-8 grid grid-cols-3 gap-4 md:gap-6 max-w-2xl">
          <Metric label="Orders" value={orders.length} />
          <Metric label="Paid Revenue" value={primaryRevenueLine} />
          <Metric label="SKUs" value={products.length} />
        </div>
      </section>

      {/* tabs */}
      <div className="px-6 md:px-12 py-6 border-b border-hairline flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setTab("orders")}
            data-testid="tab-orders"
            className={`inline-flex items-center gap-2 overline rounded-full px-4 py-2 border transition-colors duration-500 ${
              tab === "orders" ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest/50"
            }`}
          >
            <ShoppingBag size={13} strokeWidth={1.6} /> Orders
          </button>
          <button
            onClick={() => setTab("products")}
            data-testid="tab-products"
            className={`inline-flex items-center gap-2 overline rounded-full px-4 py-2 border transition-colors duration-500 ${
              tab === "products" ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest/50"
            }`}
          >
            <Package size={13} strokeWidth={1.6} /> Products & Prices
          </button>
          <button
            onClick={() => setTab("categories")}
            data-testid="tab-categories"
            className={`inline-flex items-center gap-2 overline rounded-full px-4 py-2 border transition-colors duration-500 ${
              tab === "categories" ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest/50"
            }`}
          >
            <LayoutGrid size={13} strokeWidth={1.6} /> Category Tiles
          </button>
          <button
            onClick={() => setTab("chats")}
            data-testid="tab-chats"
            className={`inline-flex items-center gap-2 overline rounded-full px-4 py-2 border transition-colors duration-500 ${
              tab === "chats" ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest/50"
            }`}
          >
            <MessageCircle size={13} strokeWidth={1.6} /> Live Chats
          </button>
          <button
            onClick={() => setTab("variations")}
            data-testid="tab-variations"
            className={`inline-flex items-center gap-2 overline rounded-full px-4 py-2 border transition-colors duration-500 ${
              tab === "variations" ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest/50"
            }`}
          >
            <Sliders size={13} strokeWidth={1.6} /> Variations
          </button>
          <button
            onClick={() => setTab("home")}
            data-testid="tab-home"
            className={`inline-flex items-center gap-2 overline rounded-full px-4 py-2 border transition-colors duration-500 ${
              tab === "home" ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest/50"
            }`}
          >
            <Home size={13} strokeWidth={1.6} /> Homepage
          </button>
          <button
            onClick={() => setTab("coupons")}
            data-testid="tab-coupons"
            className={`inline-flex items-center gap-2 overline rounded-full px-4 py-2 border transition-colors duration-500 ${
              tab === "coupons" ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest/50"
            }`}
          >
            <Ticket size={13} strokeWidth={1.6} /> Coupons
          </button>

          {tab === "products" && (
            <>
              <button
                onClick={() => setShowNewProduct(true)}
                data-testid="new-product-btn"
                className="ml-2 inline-flex items-center gap-2 overline rounded-full px-4 py-2 bg-champagne text-forest hover:bg-[hsl(34_45%_54%)] transition-colors duration-500"
              >
                <Plus size={13} strokeWidth={1.8} /> New product
              </button>
              <button
                onClick={() => setShowBulk(true)}
                data-testid="bulk-upload-btn"
                className="inline-flex items-center gap-2 overline rounded-full px-4 py-2 border border-champagne text-forest hover:bg-champagne/20 transition-colors duration-500"
              >
                <UploadCloud size={13} strokeWidth={1.8} /> Bulk upload
              </button>
              <button
                onClick={() => generateAllMissing("product")}
                data-testid="generate-missing-products"
                disabled={!!genRun}
                className="inline-flex items-center gap-2 overline rounded-full px-4 py-2 border border-champagne text-forest hover:bg-champagne/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-500"
                title="AI-generate a studio photo for every product without one"
              >
                <Sparkles size={13} strokeWidth={1.8} /> Generate all missing
              </button>
            </>
          )}
          {tab === "orders" && (
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-awb-btn"
              className="ml-2 inline-flex items-center gap-2 overline rounded-full px-4 py-2 border border-champagne text-forest hover:bg-champagne/20 transition-colors duration-500"
              title="Paste a CSV of order_id,courier,awb,eta to update many orders at once"
            >
              <UploadCloud size={13} strokeWidth={1.8} /> Bulk AWB
            </button>
          )}
          {tab === "categories" && (
            <button
              onClick={() => generateAllMissing("category")}
              data-testid="generate-missing-categories"
              disabled={!!genRun}
              className="ml-2 inline-flex items-center gap-2 overline rounded-full px-4 py-2 border border-champagne text-forest hover:bg-champagne/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-500"
              title="AI-generate a tile for every category without one"
            >
              <Sparkles size={13} strokeWidth={1.8} /> Generate all missing
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 border-hairline rounded-full px-3 py-1.5 w-full md:w-80">
          <Search size={14} strokeWidth={1.6} className="text-forest/50" />
          <input
            data-testid="admin-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={
              tab === "orders" ? "Search orders, buyers…" :
              tab === "products" ? "Search products…" :
              "Search categories…"
            }
            className="w-full bg-transparent outline-none text-sm text-forest"
          />
        </div>
      </div>

      <section className="px-6 md:px-12 py-8">
        {loading && <p className="text-forest/60">Loading…</p>}
        {!loading && tab === "orders" && (
          <>
            <OrderFilters filter={orderFilter} setFilter={setOrderFilter} />
            <OrdersTable
              orders={filteredOrders}
              formatMoney={formatMoney}
              onPatch={updateOrder}
              onOpen={setSelectedOrder}
            />
          </>
        )}
        {!loading && tab === "products" && (
          <ProductsTable
            products={filteredProducts}
            onSave={updatePrice}
            onUploadImage={uploadProductImage}
            onResetImage={resetProductImage}
            onGenerateImage={generateProductImage}
            onDelete={deleteProduct}
            onRestore={restoreProduct}
            onPatchField={patchProductField}
            onEdit={setEditingProduct}
            onZoom={openZoom}
            categories={categories}
          />
        )}
        {!loading && tab === "categories" && (
          <CategoriesGrid
            categories={filteredCategories}
            onUpload={uploadCategoryImage}
            onReset={resetCategoryImage}
            onGenerate={generateCategoryImage}
            onRename={renameCategory}
            onSort={updateCategorySort}
            onDelete={deleteCategory}
            onRestore={restoreCategory}
            onCreate={createCategory}
          />
        )}
        {tab === "chats" && <AdminChats authAxios={authAxios} />}
        {tab === "variations" && <VariationSchemaEditor authAxios={authAxios} categories={categories} />}
        {tab === "home" && <HomepageEditor authAxios={authAxios} />}
        {tab === "coupons" && <CouponsPanel authAxios={authAxios} />}
      </section>

      <NewProductModal
        open={showNewProduct || !!editingProduct}
        editing={editingProduct}
        onClose={() => { setShowNewProduct(false); setEditingProduct(null); }}
        onCreate={createProduct}
        onUpdate={updateProduct}
        categories={categories}
      />
      <BulkPhotoUploader
        open={showBulk}
        onClose={() => setShowBulk(false)}
        products={products}
        onUpload={bulkUploadOne}
      />
      {genRun && <GenerateProgress run={genRun} />}
      <ImageZoomLightbox state={zoomState} onClose={() => setZoomState(null)} />
      <BulkAWBModal open={bulkOpen} onClose={() => setBulkOpen(false)} onUpload={bulkUpload} />

      <OrderDetailDrawer
        open={!!selectedOrder}
        order={selectedOrder}
        token={token}
        onClose={() => setSelectedOrder(null)}
        onPatch={(fresh) => {
          if (!fresh) return;
          setOrders((list) => list.map((o) => (o.order_id === fresh.order_id ? fresh : o)));
          setSelectedOrder(fresh);
        }}
      />
    </main>
  );
};

const GenerateProgress = ({ run }) => {
  const pct = Math.round(((run.done + run.failed) / run.total) * 100);
  return (
    <div
      data-testid="generate-progress"
      className="fixed bottom-6 right-6 z-[55] w-[360px] bg-forest text-white shadow-2xl border border-white/10 overflow-hidden"
    >
      <div className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="overline text-champagne mb-1">§ AI Studio — {run.kind === "product" ? "Products" : "Categories"}</div>
            <div className="font-display text-white text-xl leading-none">
              {run.done + run.failed} <span className="text-white/50">/</span> {run.total}
              <span className="ml-2 font-editorial italic text-champagne text-base">generating…</span>
            </div>
          </div>
          <button
            onClick={run.cancel}
            data-testid="generate-cancel"
            className="overline text-champagne border border-champagne/50 rounded-full px-3 py-1.5 hover:bg-champagne hover:text-forest transition-colors"
          >
            Stop
          </button>
        </div>
        <div className="mt-3 h-1.5 bg-white/10 overflow-hidden rounded-full">
          <div
            className="h-full bg-champagne transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-3 text-xs text-white/60 truncate font-mono" title={run.current}>
          ↳ {run.current}
        </div>
        {run.failed > 0 && (
          <div className="mt-1 text-xs text-[hsl(0_60%_75%)]">{run.failed} failed</div>
        )}
      </div>
    </div>
  );
};

const Metric = ({ label, value }) => (
  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="bg-white border-hairline p-5">
    <div className="overline text-forest/60">{label}</div>
    <div className="font-display text-forest text-3xl md:text-4xl leading-none mt-2 tabnum">{value}</div>
  </motion.div>
);

const StatusBadge = ({ label, tone }) => (
  <span
    className={`overline rounded-full px-2 py-1 whitespace-nowrap ${
      tone === "green" ? "bg-forest text-white" :
      tone === "gold" ? "bg-gold text-white" :
      tone === "red" ? "bg-[hsl(0_60%_45%)] text-white" :
      "bg-beige text-forest"
    }`}
  >
    {label}
  </span>
);

const OrderFilters = ({ filter, setFilter }) => {
  const set = (k, v) => setFilter((f) => ({ ...f, [k]: v }));
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <select
        value={filter.payment}
        onChange={(e) => set("payment", e.target.value)}
        data-testid="filter-payment-status"
        className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest focus:border-forest focus:outline-none"
      >
        <option value="">All payments</option>
        <option value="paid">Paid</option>
        <option value="pending">Pending</option>
        <option value="failed">Failed</option>
        <option value="refunded">Refunded</option>
      </select>
      <select
        value={filter.fulfillment}
        onChange={(e) => set("fulfillment", e.target.value)}
        data-testid="filter-fulfillment"
        className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest focus:border-forest focus:outline-none"
      >
        <option value="">All fulfilment</option>
        {FULFILL_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
      </select>
      <label className="flex items-center gap-2 text-xs text-forest/60">
        From
        <input
          type="date" value={filter.from}
          onChange={(e) => set("from", e.target.value)}
          data-testid="filter-date-from"
          className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest focus:border-forest focus:outline-none"
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-forest/60">
        To
        <input
          type="date" value={filter.to}
          onChange={(e) => set("to", e.target.value)}
          data-testid="filter-date-to"
          className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest focus:border-forest focus:outline-none"
        />
      </label>
      {(filter.payment || filter.fulfillment || filter.from || filter.to) && (
        <button
          type="button"
          onClick={() => setFilter({ payment: "", fulfillment: "", from: "", to: "" })}
          data-testid="filter-clear"
          className="overline text-forest/70 text-[10px] tracking-[2px] hover:text-forest"
        >
          Clear filters
        </button>
      )}
    </div>
  );
};

const OrdersTable = ({ orders, formatMoney, onPatch, onOpen }) => {
  if (!orders.length) {
    return (
      <div className="py-20 text-center">
        <p className="font-display text-forest text-3xl">No orders <span className="font-editorial italic text-champagne">yet.</span></p>
        <p className="text-forest/60 mt-3">Once a buyer checks out, they&rsquo;ll show here.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="text-forest/60 overline border-b border-hairline">
            <th className="py-3 pr-4">Order</th>
            <th className="py-3 pr-4">Buyer</th>
            <th className="py-3 pr-4">Gateway</th>
            <th className="py-3 pr-4">Total</th>
            <th className="py-3 pr-4">Paid</th>
            <th className="py-3 pr-4">Payment</th>
            <th className="py-3 pr-4">Fulfilment</th>
            <th className="py-3 pr-4">Courier</th>
            <th className="py-3 pr-4">Tracking / AWB</th>
            <th className="py-3 pr-4">Est. Delivery</th>
            <th className="py-3 pr-4">Date</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const cur = o.currency || "USD";
            const calc = Number(o.total ?? o.subtotal ?? 0);
            const paid = o.paid_amount != null ? Number(o.paid_amount) : null;
            const paidCur = o.paid_currency || cur;
            const mismatch = paid != null && Math.abs(paid - calc) > 0.01;
            return (
              <tr
                key={o.order_id}
                className="border-b border-hairline align-top hover:bg-bone/40 cursor-pointer"
                data-testid={`order-row-${o.order_id}`}
                onClick={(e) => {
                  // Ignore clicks that hit an editable control.
                  if (e.target.closest("select,input,button,a")) return;
                  onOpen?.(o);
                }}
              >
                <td className="py-4 pr-4">
                  <div className="font-display text-forest">{o.order_id}</div>
                  <div className="text-forest/50 text-xs">{(o.items || []).length} items</div>
                  {o.coupon_code && (
                    <div className="text-gold text-[10px] mt-1 overline">{o.coupon_code}</div>
                  )}
                </td>
                <td className="py-4 pr-4">
                  <div className="text-forest">{o.buyer?.name || o.paypal_payer_name || "—"}</div>
                  <div className="text-forest/60 text-xs">{o.buyer?.email || o.paypal_payer_email || "—"}</div>
                  <div className="text-forest/50 text-xs">{o.buyer?.phone || ""}</div>
                </td>
                <td className="py-4 pr-4 uppercase text-forest text-xs">{o.gateway || "—"}</td>
                <td className="py-4 pr-4 tabnum text-forest" data-testid={`order-calc-${o.order_id}`}>
                  {formatMoney(calc, cur)}
                  <div className="text-forest/50 text-xs">{cur}</div>
                </td>
                <td className="py-4 pr-4 tabnum" data-testid={`order-paid-${o.order_id}`}>
                  {paid != null ? (
                    <>
                      <div className={mismatch ? "text-amber-700 font-medium" : "text-emerald-700"}>
                        {formatMoney(paid, paidCur)}
                      </div>
                      <div className="text-forest/50 text-xs">{paidCur}</div>
                    </>
                  ) : (
                    <span className="text-forest/40 text-xs">—</span>
                  )}
                </td>
                <td className="py-4 pr-4">
                  <StatusBadge
                    label={o.payment_status || "pending"}
                    tone={o.payment_status === "paid" ? "green" : "gold"}
                  />
                </td>
                <td className="py-4 pr-4">
                  <select
                    data-testid={`order-fulfill-${o.order_id}`}
                    value={o.fulfillment_status || "pending"}
                    onChange={(e) => onPatch(o.order_id, { fulfillment_status: e.target.value })}
                    className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest focus:border-forest focus:outline-none"
                  >
                    {FULFILL_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td className="py-4 pr-4">
                  <select
                    data-testid={`order-courier-${o.order_id}`}
                    value={o.courier_name || ""}
                    onChange={(e) => onPatch(o.order_id, { courier_name: e.target.value })}
                    className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest focus:border-forest focus:outline-none"
                  >
                    {COURIERS.map((c) => <option key={c || "none"} value={c}>{c || "— none —"}</option>)}
                  </select>
              </td>
              <td className="py-4 pr-4">
                <TrackingInput
                  value={o.tracking_number || ""}
                  onSave={(v) => onPatch(o.order_id, { tracking_number: v })}
                  testid={`order-tracking-${o.order_id}`}
                />
              </td>
              <td className="py-4 pr-4">
                <ETAInput
                  value={o.estimated_delivery || ""}
                  onSave={(v) => onPatch(o.order_id, { estimated_delivery: v })}
                  testid={`order-eta-${o.order_id}`}
                />
              </td>
              <td className="py-4 pr-4 text-forest/70 text-xs whitespace-nowrap">
                {o.created_at ? new Date(o.created_at).toLocaleString() : "—"}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TrackingInput = ({ value, onSave, testid }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div className="flex items-center gap-1">
      <input
        data-testid={testid}
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="AWB / Tracking #"
        className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest w-40"
      />
      {v !== value && (
        <button onClick={() => onSave(v)} className="p-1.5 rounded-full bg-forest text-white" title="Save">
          <Check size={12} strokeWidth={1.6} />
        </button>
      )}
    </div>
  );
};

const ETAInput = ({ value, onSave, testid }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div className="flex items-center gap-1">
      <input
        type="date"
        data-testid={testid}
        value={v}
        onChange={(e) => setV(e.target.value)}
        className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest w-36"
      />
      {v !== value && (
        <button onClick={() => onSave(v)} className="p-1.5 rounded-full bg-forest text-white" title="Save">
          <Check size={12} strokeWidth={1.6} />
        </button>
      )}
    </div>
  );
};

const BULK_SAMPLE = `order_id,courier,awb,eta,status
AN-XXXXXXXX01,FedEx,784212345678,2026-09-05,shipped
AN-XXXXXXXX02,DHL,GM123456789,2026-09-04,shipped
AN-XXXXXXXX03,Delhivery,DL987654321,,out_for_delivery`;

const BulkAWBModal = ({ open, onClose, onUpload }) => {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!open) return null;
  const submit = async () => {
    if (!csv.trim()) return;
    setBusy(true); setResult(null);
    try {
      const data = await onUpload(csv);
      setResult(data);
    } catch { /* toast already fired */ }
    finally { setBusy(false); }
  };
  const close = () => { setCsv(""); setResult(null); onClose(); };
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="bulk-awb-modal"
      className="fixed inset-0 z-[80] bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={close}
    >
      <div className="bg-white w-full max-w-3xl border border-hairline shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <div>
            <div className="overline text-gold text-[10px] tracking-[3px]">§ Bulk AWB Upload</div>
            <div className="font-display text-forest text-2xl leading-none mt-1">Update many orders at once</div>
          </div>
          <button onClick={close} className="p-2 hover:bg-bone rounded-full text-forest" aria-label="Close">
            <X size={16} strokeWidth={1.6} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm text-forest/80">
          <div>
            Paste a CSV with a header row. Required column: <span className="font-mono text-forest">order_id</span>.
            Recognised columns: <span className="font-mono text-forest">courier · awb · eta · status · note</span>.
            Every row that changes fulfillment_status will trigger the customer email automatically.
          </div>

          <details className="border border-hairline rounded-md">
            <summary className="cursor-pointer px-3 py-2 text-forest/70 overline text-[10px] tracking-[2px]">Show sample CSV</summary>
            <pre className="px-3 py-3 text-xs text-forest/80 whitespace-pre overflow-auto bg-bone">{BULK_SAMPLE}</pre>
          </details>

          <textarea
            data-testid="bulk-awb-textarea"
            rows={10}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={BULK_SAMPLE}
            className="w-full font-mono text-xs text-forest bg-bone border border-hairline rounded-md p-3 outline-none focus:border-forest"
            spellCheck={false}
          />

          {result && (
            <div className="border border-hairline rounded-md p-3 bg-bone" data-testid="bulk-awb-result">
              <div className="overline text-[10px] tracking-[2px] text-gold mb-2">§ Results</div>
              <div className="text-forest/80 text-sm">
                <strong className="text-forest">{(result.updated || []).length}</strong> updated ·{" "}
                <strong className="text-forest">{(result.errors || []).length}</strong> errors ·{" "}
                <strong className="text-forest">{result.total || 0}</strong> rows total
              </div>
              {(result.errors || []).length > 0 && (
                <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-red-700 space-y-1">
                  {(result.errors || []).map((e, i) => (
                    <li key={i} data-testid={`bulk-awb-error-${i}`}>Row {e.row} · <span className="font-mono">{e.order_id || "(no id)"}</span> — {e.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-hairline flex items-center justify-end gap-3">
          <button onClick={close} className="overline text-forest/70 border border-hairline rounded-full px-5 py-2 hover:bg-bone">Close</button>
          <button
            onClick={submit}
            disabled={busy || !csv.trim()}
            data-testid="bulk-awb-submit"
            className="overline bg-forest text-white rounded-full px-6 py-2.5 hover:bg-[hsl(150_60%_16%)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Uploading…" : "Update & email buyers"}
          </button>
        </div>
      </div>
    </div>
  );
};



const ProductsTable = ({ products, onSave, onUploadImage, onResetImage, onGenerateImage, onDelete, onRestore, onPatchField, onEdit, onZoom, categories }) => {
  if (!products.length) return <p className="text-forest/60">No products.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="text-forest/60 overline border-b border-hairline">
            <th className="py-3 pr-4">Photo</th>
            <th className="py-3 pr-4">Product</th>
            <th className="py-3 pr-4">Category</th>
            <th className="py-3 pr-4">Price (INR)</th>
            <th className="py-3 pr-4">Stock</th>
            <th className="py-3 pr-4">Sort</th>
            <th className="py-3 pr-4">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <ProductRow
              key={p.slug}
              p={p}
              onSave={onSave}
              onUploadImage={onUploadImage}
              onResetImage={onResetImage}
              onGenerateImage={onGenerateImage}
              onDelete={onDelete}
              onRestore={onRestore}
              onPatchField={onPatchField}
              onEdit={onEdit}
              onZoom={onZoom}
              categories={categories}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProductRow = ({ p, onSave, onUploadImage, onResetImage, onGenerateImage, onDelete, onRestore, onPatchField, onEdit, onZoom, categories }) => {
  const [priceStr, setPriceStr] = useState(String(p.price));
  const [stockStr, setStockStr] = useState(p.stock == null ? "" : String(p.stock));
  const [sortStr, setSortStr] = useState(String(p.sort_order ?? ""));
  useEffect(() => { setPriceStr(String(p.price)); }, [p.price]);
  useEffect(() => { setStockStr(p.stock == null ? "" : String(p.stock)); }, [p.stock]);
  useEffect(() => { setSortStr(String(p.sort_order ?? "")); }, [p.sort_order]);
  const priceDirty = Number(priceStr) !== Number(p.price) && !isNaN(Number(priceStr)) && Number(priceStr) > 0;
  const stockDirty = String(stockStr) !== String(p.stock ?? "") && stockStr !== "" && !isNaN(Number(stockStr));
  const sortDirty = String(sortStr) !== String(p.sort_order ?? "") && sortStr !== "" && !isNaN(Number(sortStr));

  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const heroSrc = p.hero_image ? resolveImage(p.hero_image) : null;
  const cats = (categories || []).filter((c) => !c.is_deleted);

  const onPick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try { await onUploadImage(p.slug, file); } finally { setBusy(false); }
  };
  const onGenerate = async () => {
    setGenBusy(true);
    try { await onGenerateImage(p.slug); } finally { setGenBusy(false); }
  };

  const rowClass = `border-b border-hairline hover:bg-bone/40 ${p.is_deleted ? "opacity-50" : ""}`;

  return (
    <tr className={rowClass}>
      <td className="py-4 pr-4 w-[110px]">
        <div className="relative w-[88px] h-[110px] bg-bone border border-hairline overflow-hidden">
          {heroSrc ? (
            <button
              type="button"
              onClick={() => onZoom?.(p)}
              className="absolute inset-0 h-full w-full group focus:outline-none"
              title="Click to zoom"
              data-testid={`zoom-product-${p.slug}`}
            >
              <img
                src={heroSrc}
                alt={p.title}
                loading="lazy"
                decoding="async"
                width="96"
                height="96"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid={`product-photo-${p.slug}`}
              />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200">
                <ZoomIn size={18} strokeWidth={1.6} className="text-white" />
              </span>
            </button>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-forest/40">
              <ImageIcon size={22} strokeWidth={1.4} />
            </div>
          )}
          {heroSrc && (
            <span className="absolute top-1 left-1 overline text-[9px] bg-gold text-white rounded-full px-1.5 py-0.5">
              Studio
            </span>
          )}
        </div>
        <div className="mt-2 flex flex-col gap-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            data-testid={`photo-input-${p.slug}`}
            onChange={onFile}
          />
          <button
            data-testid={`photo-upload-${p.slug}`}
            onClick={onPick}
            disabled={busy || genBusy}
            className="inline-flex items-center justify-center gap-1 overline text-[10px] rounded-full px-2 py-1 bg-forest text-white disabled:opacity-40"
          >
            <Upload size={11} strokeWidth={1.8} /> {busy ? "…" : heroSrc ? "Replace" : "Upload"}
          </button>
          <button
            data-testid={`photo-generate-${p.slug}`}
            onClick={onGenerate}
            disabled={busy || genBusy}
            title="Generate a studio-style photo with AI (Nano Banana)"
            className="inline-flex items-center justify-center gap-1 overline text-[10px] rounded-full px-2 py-1 bg-champagne text-forest disabled:opacity-40"
          >
            <Sparkles size={11} strokeWidth={1.8} /> {genBusy ? "…" : "AI"}
          </button>
          {heroSrc && (
            <button
              data-testid={`photo-reset-${p.slug}`}
              onClick={() => onResetImage(p.slug)}
              disabled={busy || genBusy}
              className="overline text-[10px] rounded-full px-2 py-1 border border-hairline text-forest/70 hover:border-forest disabled:opacity-40"
            >
              Revert
            </button>
          )}
        </div>
      </td>
      <td className="py-4 pr-4">
        <div className="font-display text-forest text-lg leading-tight">{p.title}</div>
        <div className="text-forest/50 text-xs font-mono">{p.slug}</div>
        <div className="mt-1 flex items-center gap-1.5">
          {p.kind === "custom" && (
            <span className="overline text-[9px] bg-forest text-white rounded-full px-1.5 py-0.5">Custom</span>
          )}
          {p.featured && (
            <span className="overline text-[9px] bg-gold text-white rounded-full px-1.5 py-0.5">Featured</span>
          )}
          {p.is_deleted && (
            <span className="overline text-[9px] bg-[hsl(0_60%_45%)] text-white rounded-full px-1.5 py-0.5">Hidden</span>
          )}
        </div>
      </td>
      <td className="py-4 pr-4">
        <select
          data-testid={`cat-select-${p.slug}`}
          value={p.category || cats[0]?.slug || ""}
          onChange={(e) => onPatchField(p.slug, { category: e.target.value })}
          className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest focus:border-forest focus:outline-none max-w-[180px]"
        >
          {cats.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
      </td>
      <td className="py-4 pr-4">
        <div className="flex items-center gap-1">
          <span className="text-forest">₹</span>
          <input
            data-testid={`price-input-${p.slug}`}
            value={priceStr}
            onChange={(e) => setPriceStr(e.target.value)}
            className="bg-transparent border border-hairline rounded-full px-2.5 py-1.5 text-sm text-forest w-24 focus:border-forest focus:outline-none tabnum"
          />
          {priceDirty && (
            <button
              data-testid={`price-save-${p.slug}`}
              onClick={() => onSave(p.slug, Number(priceStr))}
              className="p-1 rounded-full bg-forest text-white"
              title="Save"
            >
              <Check size={11} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </td>
      <td className="py-4 pr-4">
        <div className="flex items-center gap-1">
          <input
            data-testid={`stock-input-${p.slug}`}
            type="number"
            min="0"
            value={stockStr}
            onChange={(e) => setStockStr(e.target.value)}
            className="bg-transparent border border-hairline rounded-full px-2.5 py-1.5 text-sm text-forest w-16 focus:border-forest focus:outline-none tabnum"
          />
          {stockDirty && (
            <button
              onClick={() => onPatchField(p.slug, { stock: Number(stockStr) })}
              className="p-1 rounded-full bg-forest text-white"
              title="Save"
            >
              <Check size={11} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </td>
      <td className="py-4 pr-4">
        <div className="flex items-center gap-1">
          <input
            data-testid={`sort-input-${p.slug}`}
            type="number"
            min="0"
            value={sortStr}
            onChange={(e) => setSortStr(e.target.value)}
            className="bg-transparent border border-hairline rounded-full px-2.5 py-1.5 text-sm text-forest w-16 focus:border-forest focus:outline-none tabnum"
          />
          {sortDirty && (
            <button
              onClick={() => onPatchField(p.slug, { sort_order: Number(sortStr) })}
              className="p-1 rounded-full bg-forest text-white"
              title="Save"
            >
              <Check size={11} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </td>
      <td className="py-4 pr-4">
        <div className="flex items-center gap-1.5">
          <button
            data-testid={`edit-${p.slug}`}
            onClick={() => onEdit(p)}
            className="inline-flex items-center gap-1 overline text-[10px] rounded-full px-2.5 py-1 bg-forest text-white"
          >
            Edit
          </button>
          {p.is_deleted ? (
            <button
              data-testid={`restore-${p.slug}`}
              onClick={() => onRestore(p.slug)}
              className="inline-flex items-center gap-1 overline text-[10px] rounded-full px-2.5 py-1 bg-forest text-white"
            >
              <RotateCcw size={11} strokeWidth={1.8} /> Restore
            </button>
          ) : (
            <button
              data-testid={`delete-${p.slug}`}
              onClick={() => onDelete(p.slug)}
              className="inline-flex items-center gap-1 overline text-[10px] rounded-full px-2.5 py-1 border border-hairline text-forest/60 hover:border-[hsl(0_60%_45%)] hover:text-[hsl(0_60%_45%)]"
            >
              <Trash2 size={11} strokeWidth={1.8} /> Hide
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const CategoriesGrid = ({ categories, onUpload, onReset, onGenerate, onRename, onSort, onDelete, onRestore, onCreate }) => {
  const [showNew, setShowNew] = useState(false);
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <p className="text-forest/60 text-sm">Reorder by editing the sort number — lower shows first.</p>
        <button
          data-testid="new-category-btn"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 overline rounded-full px-4 py-2 bg-champagne text-forest hover:bg-[hsl(34_45%_54%)]"
        >
          <Plus size={13} strokeWidth={1.8} /> New category
        </button>
      </div>
      {showNew && <NewCategoryForm onCancel={() => setShowNew(false)} onCreate={async (payload) => { await onCreate(payload); setShowNew(false); }} />}
      {categories.length === 0 ? (
        <p className="text-forest/60">No categories.</p>
      ) : (
        <div data-testid="categories-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {categories.map((c, i) => (
            <CategoryTileCard
              key={c.slug}
              c={c}
              i={i}
              onUpload={onUpload}
              onReset={onReset}
              onGenerate={onGenerate}
              onRename={onRename}
              onSort={onSort}
              onDelete={onDelete}
              onRestore={onRestore}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const NewCategoryForm = ({ onCreate, onCancel }) => {
  const [label, setLabel] = useState("");
  const [slug, setSlug] = useState("");
  const [autoSlug, setAutoSlug] = useState(true);
  const [sort, setSort] = useState(500);
  const [saving, setSaving] = useState(false);
  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim() || !slug.trim()) return toast.error("Label + slug required");
    setSaving(true);
    try { await onCreate({ slug, label: label.trim(), sort_order: Number(sort) || 500 }); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="mb-6 border border-hairline bg-bone p-4 grid grid-cols-12 gap-3">
      <div className="col-span-12 md:col-span-4">
        <div className="overline text-forest/60 mb-1">Label</div>
        <input
          data-testid="nc-label"
          value={label}
          onChange={(e) => { setLabel(e.target.value); if (autoSlug) setSlug(slugify(e.target.value)); }}
          placeholder="Lampshades"
          className="w-full bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
        />
      </div>
      <div className="col-span-8 md:col-span-4">
        <div className="overline text-forest/60 mb-1">Slug</div>
        <input
          data-testid="nc-slug"
          value={slug}
          onChange={(e) => { setAutoSlug(false); setSlug(slugify(e.target.value)); }}
          placeholder="lampshades"
          className="w-full bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest font-mono focus:outline-none focus:border-forest"
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <div className="overline text-forest/60 mb-1">Sort</div>
        <input
          type="number"
          min="0"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="w-full bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest tabnum focus:outline-none focus:border-forest"
        />
      </div>
      <div className="col-span-12 md:col-span-2 flex items-end gap-2">
        <button
          type="submit"
          data-testid="nc-submit"
          disabled={saving}
          className="flex-1 inline-flex items-center justify-center gap-1 overline text-white bg-forest rounded-full px-4 py-2 disabled:opacity-40"
        >
          <Plus size={11} strokeWidth={1.8} /> {saving ? "…" : "Add"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="overline text-forest/60 border border-hairline rounded-full px-3 py-2 hover:border-forest"
        >
          <X size={12} strokeWidth={1.8} />
        </button>
      </div>
    </form>
  );
};

const CategoryTileCard = ({ c, i, onUpload, onReset, onGenerate, onRename, onSort, onDelete, onRestore }) => {
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [label, setLabel] = useState(c.label);
  const [sort, setSort] = useState(String(c.sort_order ?? 100));
  useEffect(() => setLabel(c.label), [c.label]);
  useEffect(() => setSort(String(c.sort_order ?? 100)), [c.sort_order]);
  const inputRef = useRef(null);
  const src = c.image ? resolveImage(c.image) : null;
  const labelDirty = label !== c.label && label.trim().length > 0;
  const sortDirty = Number(sort) !== Number(c.sort_order ?? 100) && !isNaN(Number(sort));

  const onPick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try { await onUpload(c.slug, file); } finally { setBusy(false); }
  };
  const onGen = async () => {
    setGenBusy(true);
    try { await onGenerate(c.slug); } finally { setGenBusy(false); }
  };

  return (
    <div className={`border border-hairline bg-white ${c.is_deleted ? "opacity-50" : ""}`}>
      <div className="relative aspect-[4/5] bg-bone overflow-hidden">
        {src ? (
          <img
            src={src}
            alt={c.label}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid={`category-photo-${c.slug}`}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-forest/40 gap-2">
            <ImageIcon size={30} strokeWidth={1.3} />
            <span className="overline text-forest/50">Stock placeholder</span>
          </div>
        )}
        <div className="absolute top-3 left-3 overline text-white/95 mix-blend-difference">
          N° {String(i + 1).padStart(2, "0")}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1">
          {src && <span className="overline bg-gold text-white rounded-full px-2 py-0.5">Studio</span>}
          {c.is_deleted && <span className="overline bg-[hsl(0_60%_45%)] text-white rounded-full px-2 py-0.5">Hidden</span>}
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <input
            data-testid={`cat-label-${c.slug}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full font-display text-forest text-lg leading-tight bg-transparent border-b border-transparent focus:border-forest focus:outline-none"
          />
          <div className="mt-0.5 text-forest/50 text-xs font-mono">{c.slug}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 flex-1">
            <span className="overline text-forest/60">Sort</span>
            <input
              data-testid={`cat-sort-${c.slug}`}
              type="number"
              min="0"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-16 bg-transparent border border-hairline rounded-full px-2 py-1 text-sm text-forest tabnum focus:border-forest focus:outline-none"
            />
          </div>
          {(labelDirty || sortDirty) && (
            <button
              onClick={() => {
                if (labelDirty) onRename(c.slug, label.trim());
                if (sortDirty) onSort(c.slug, Number(sort));
              }}
              data-testid={`cat-save-${c.slug}`}
              className="inline-flex items-center gap-1 overline text-xs rounded-full px-2.5 py-1 bg-forest text-white"
            >
              <Check size={11} strokeWidth={1.8} /> Save
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          data-testid={`cat-input-${c.slug}`}
          onChange={onFile}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid={`cat-upload-${c.slug}`}
            onClick={onPick}
            disabled={busy || genBusy}
            className="inline-flex items-center gap-1 overline text-xs rounded-full px-3 py-1.5 bg-forest text-white disabled:opacity-40"
          >
            <Upload size={11} strokeWidth={1.8} /> {busy ? "…" : src ? "Replace" : "Upload"}
          </button>
          <button
            data-testid={`cat-generate-${c.slug}`}
            onClick={onGen}
            disabled={busy || genBusy}
            title="Generate a studio-style tile with AI (Nano Banana)"
            className="inline-flex items-center gap-1 overline text-xs rounded-full px-3 py-1.5 bg-champagne text-forest disabled:opacity-40"
          >
            <Sparkles size={11} strokeWidth={1.8} /> {genBusy ? "…" : "AI"}
          </button>
          {src && (
            <button
              data-testid={`cat-reset-${c.slug}`}
              onClick={() => onReset(c.slug)}
              className="overline text-xs rounded-full px-3 py-1.5 border border-hairline text-forest/70 hover:border-forest"
            >
              Revert
            </button>
          )}
          {c.is_deleted ? (
            <button
              data-testid={`cat-restore-${c.slug}`}
              onClick={() => onRestore(c.slug)}
              className="inline-flex items-center gap-1 overline text-xs rounded-full px-3 py-1.5 bg-forest text-white"
            >
              <RotateCcw size={11} strokeWidth={1.8} /> Restore
            </button>
          ) : (
            <button
              data-testid={`cat-delete-${c.slug}`}
              onClick={() => onDelete(c.slug)}
              className="inline-flex items-center gap-1 overline text-xs rounded-full px-3 py-1.5 border border-hairline text-forest/60 hover:border-[hsl(0_60%_45%)] hover:text-[hsl(0_60%_45%)]"
            >
              <Trash2 size={11} strokeWidth={1.8} /> Hide
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
